import React, {useCallback, useContext, useEffect, useMemo, useState} from 'react';
import axios from 'axios';
import {useSearchParams} from 'react-router-dom';
import {AuthContext} from '../auth/ContextAuthentication';
import {ADMIN_CAPABILITIES} from '../auth/adminCapabilities';

const TABS = ['summary', 'payments', 'subscriptions', 'recurring-users', 'reconciliation', 'benefit-cost', 'campaign-attribution'];
const ENDPOINTS = {
  payments: 'api/premium/admin/reports/payments',
  subscriptions: 'api/premium/admin/reports/subscriptions',
  'recurring-users': 'api/premium/admin/reports/recurring-users',
  reconciliation: 'api/premium/admin/reports/reconciliation',
  'benefit-cost': 'api/premium/admin/reports/benefit-cost',
  'campaign-attribution': 'api/premium/admin/reports/campaign-attribution',
};

const karachiDate = (date) => new Intl.DateTimeFormat('en-CA', {
  timeZone: 'Asia/Karachi', year: 'numeric', month: '2-digit', day: '2-digit',
}).format(date);

const rangeForPreset = (preset) => {
  const now = new Date();
  const today = karachiDate(now);
  const ago = (days) => karachiDate(new Date(now.getTime() - days * 86400000));
  if (preset === 'today') return {from: today, to: today};
  if (preset === 'yesterday') return {from: ago(1), to: ago(1)};
  if (preset === '7d') return {from: ago(6), to: today};
  if (preset === '30d') return {from: ago(29), to: today};
  const parts = today.split('-').map(Number);
  if (preset === 'month') return {from: `${parts[0]}-${String(parts[1]).padStart(2, '0')}-01`, to: today};
  const firstThisMonth = new Date(Date.UTC(parts[0], parts[1] - 1, 1));
  const lastPreviousMonth = new Date(firstThisMonth.getTime() - 86400000);
  const previousEnd = karachiDate(lastPreviousMonth);
  return {from: `${previousEnd.slice(0, 7)}-01`, to: previousEnd};
};

const money = (value) => `PKR ${Number(value || 0).toLocaleString(undefined, {maximumFractionDigits: 2})}`;
const percent = (value) => `${(Number(value || 0) * 100).toFixed(1)}%`;
const dateTime = (value) => value ? new Date(value).toLocaleString('en-PK', {timeZone: 'Asia/Karachi'}) : '—';

const Metric = ({label, value, help}) => (
  <div className="rounded bg-white p-4 shadow" title={help || ''}>
    <p className="text-xs text-gray-500">{label}</p>
    <p className="mt-1 text-xl font-semibold">{value}</p>
    {help && <p className="mt-2 text-xs text-gray-500">{help}</p>}
  </div>
);

const userLabel = (row) => row?.user ? `${row.user.name || 'Customer'} ${row.user.phone || ''}`.trim() : '—';

const tableColumns = {
  payments: [
    ['Customer', (row) => userLabel(row)], ['Plan', (row) => row.plan?.displayName || row.plan?.code || '—'],
    ['Purpose', (row) => row.purpose], ['Amount', (row) => money(row.amount)],
    ['Status', (row) => `${row.internalStatus || '—'} / ${row.providerStatus || '—'}`],
    ['Completed', (row) => dateTime(row.completedAt || row.createdAt)], ['Attention', (row) => row.attentionReason || '—'],
  ],
  subscriptions: [
    ['Customer', (row) => userLabel(row)], ['Plan', (row) => row.planSnapshot?.displayName || row.planSnapshot?.code || '—'],
    ['Status', (row) => row.status], ['Origin', (row) => row.origin], ['Period end', (row) => dateTime(row.currentPeriodEnd)],
    ['Renewals', (row) => row.completedRenewals], ['Failed renewals', (row) => row.failedRenewals],
    ['Lifetime', (row) => money(row.lifetimeCollections)],
  ],
  'recurring-users': [
    ['Customer', (row) => userLabel(row)], ['Plan', (row) => row.planSnapshot?.displayName || row.planSnapshot?.code || '—'],
    ['Status', (row) => row.subscriptionStatus], ['Renewals', (row) => row.successfulRenewals],
    ['Failed renewals', (row) => row.failedRenewals], ['Last renewal', (row) => dateTime(row.lastRenewalAt)],
    ['Next renewal', (row) => dateTime(row.nextRenewalAt)], ['Lifetime', (row) => money(row.lifetimeCollections)],
  ],
  reconciliation: [
    ['Severity', (row) => row.severity], ['Type', (row) => row.type], ['Customer', (row) => userLabel(row)],
    ['Payment / subscription', (row) => row.paymentId || row.subscriptionId || row.webhookEventId || '—'],
    ['Occurred', (row) => dateTime(row.occurredAt)], ['Reason', (row) => row.reason],
  ],
  'benefit-cost': [
    ['Customer', (row) => userLabel(row)], ['Benefit', (row) => row.benefitCode],
    ['Gross credited', (row) => money(row.creditedAmount)], ['Reversed', (row) => money(row.reversedAmount)],
    ['Net cost', (row) => money(row.netCost)], ['Status', (row) => row.status],
    ['Booking', (row) => `${row.bookingModel || ''} ${row.bookingId || ''}`.trim() || '—'],
    ['Credited at', (row) => dateTime(row.completedAt)], ['Reversed at', (row) => dateTime(row.reversedAt)],
    ['Reason', (row) => row.failureReason || '—'],
  ],
};

const DataTable = ({tab, rows}) => {
  const columns = tableColumns[tab] || [];
  if (!rows.length) return <div className="rounded bg-white p-6 text-sm text-gray-500 shadow">No records match this report.</div>;
  return (
    <div className="overflow-x-auto rounded bg-white shadow">
      <table className="min-w-full text-sm">
        <thead><tr className="text-left text-gray-500">{columns.map(([label]) => <th key={label} className="p-3">{label}</th>)}</tr></thead>
        <tbody>{rows.map((row, index) => <tr key={row.id || row.paymentId || row.subscriptionId || row.webhookEventId || index} className="border-t">
          {columns.map(([label, render]) => <td key={label} className="max-w-xs p-3 align-top">{render(row)}</td>)}
        </tr>)}</tbody>
      </table>
    </div>
  );
};

const reportTh = 'border border-[#0a5c33] bg-[#0C7240] px-2 py-2 text-center text-xs font-semibold text-white';
const reportTd = 'border border-gray-300 px-2 py-2 text-center text-sm text-gray-800';
const reportTdLeft = 'border border-gray-300 px-2 py-2 text-left text-sm text-gray-800';
const PLATFORM_LABELS = {
  t: 'TikTok',
  tiktok: 'TikTok',
  tt: 'TikTok',
  m: 'Meta',
  meta: 'Meta',
  fb: 'Meta',
  facebook: 'Meta',
  g: 'Google',
  google: 'Google',
  s: 'Snapchat',
  snap: 'Snapchat',
  snapchat: 'Snapchat',
  d: 'Direct',
  direct: 'Direct',
  organic: 'Direct',
};

const displayDim = (value) => {
  const text = String(value || '').trim();
  if (!text || text.toLowerCase() === 'unknown') return 'default';
  return text;
};

/** Platform: T → TikTok, M → Meta, etc. (also handles legacy short codes). */
const displayPlatformDim = (value) => {
  const text = displayDim(value);
  if (text === 'default') return text;
  return PLATFORM_LABELS[text.toLowerCase()] || text;
};

/** Plan IDs: bare numbers (e.g. package=2) show as "planid 2"; empty → default. */
const displayPackageDim = (value) => {
  const text = displayDim(value);
  if (text === 'default') return text;
  if (/^\d+$/.test(text)) return `planid ${text}`;
  if (/^planid$/i.test(text)) return text; // legacy key without number
  return text;
};

const rowDimValue = (row, rawKey, displayKey) => {
  if (!row) return 0;
  if (row[displayKey] != null) return Number(row[displayKey] || 0);
  if (row[rawKey] != null) return Number(row[rawKey] || 0);
  if (displayKey === 'default' || rawKey === 'unknown' || rawKey === 'default') {
    return Number(row.default || row.unknown || 0);
  }
  return 0;
};

const DetailedSliceTable = ({title, rows, columns}) => {
  const rawCols = columns.length ? columns : ['—'];
  const cols = rawCols.map(displayDim);
  const footerTotals = rawCols.map((column, index) =>
    (rows || []).reduce((sum, row) => sum + rowDimValue(row, column, cols[index]), 0),
  );
  const footerTotal = (rows || []).reduce((sum, row) => sum + Number(row.total || 0), 0);
  return (
    <div className="overflow-x-auto rounded border border-gray-300 bg-white">
      <table className="min-w-full border-collapse text-sm">
        <thead>
          <tr>
            <th className={reportTh} colSpan={cols.length + 2}>{displayDim(title)}</th>
          </tr>
          <tr>
            <th className={reportTh}>Date</th>
            {cols.map((column) => <th key={column} className={reportTh}>{column}</th>)}
            <th className={reportTh}>Total</th>
          </tr>
        </thead>
        <tbody>
          {!rows?.length ? (
            <tr>
              <td className={`${reportTd} text-gray-500`} colSpan={cols.length + 2}>No data</td>
            </tr>
          ) : rows.map((row) => (
            <tr key={row.date}>
              <td className={reportTdLeft}>{row.date}</td>
              {rawCols.map((column, index) => (
                <td key={`${row.date}-${column}`} className={reportTd}>
                  {rowDimValue(row, column, cols[index])}
                </td>
              ))}
              <td className={`${reportTd} font-semibold`}>{Number(row.total || 0)}</td>
            </tr>
          ))}
          {rows?.length > 0 && (
            <tr className="bg-gray-50">
              <td className={`${reportTdLeft} font-semibold`}>Sub Total</td>
              {footerTotals.map((value, index) => (
                <td key={`footer-${rawCols[index]}`} className={`${reportTd} font-semibold`}>{value}</td>
              ))}
              <td className={`${reportTd} font-semibold`}>{footerTotal}</td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
};

const CampaignAttributionReport = ({report}) => {
  if (!report) return <div className="rounded bg-white p-6 text-sm text-gray-500 shadow">Loading campaign attribution…</div>;
  const dimensions = report.metadata?.dimensions || {};
  const platforms = (dimensions.platforms || []).map(displayPlatformDim);
  const packages = (dimensions.packages || []).map(displayPackageDim);
  const parameters = (dimensions.parameters || []).map(displayDim);
  // Keep original keys for row lookups when backend still sends "unknown".
  const rawPlatforms = dimensions.platforms || [];
  const rawPackages = dimensions.packages || [];
  const rawParameters = dimensions.parameters || [];
  const dailyWide = report.dailyWide || [];
  const packageColSpan = packages.length;
  const parameterColSpan = parameters.length;
  // Total Sub under Platform (old) + Total Sub again at the far end.
  const hasPlatformTotalSub = platforms.length > 0;
  const hasEndTotalSub = platforms.length > 0 || packages.length > 0 || parameters.length > 0;
  const platformColSpan = platforms.length + (hasPlatformTotalSub ? 1 : 0);
  const subsColSpan =
    platformColSpan + packageColSpan + parameterColSpan + (hasEndTotalSub ? 1 : 0);

  const rowTotal = (row) => Number(row.total || row.platform?.total || 0);

  const cellForDim = (row, dim, rawKey, displayKey) => {
    // Prefer display key, then raw key (unknown ↔ default / T ↔ TikTok).
    const bag = row?.[dim] || {};
    if (bag[displayKey] != null) return Number(bag[displayKey] || 0);
    if (bag[rawKey] != null) return Number(bag[rawKey] || 0);
    if (displayKey === 'default') return Number(bag.unknown || bag.default || 0);
    if (displayKey === 'TikTok') return Number(bag.T || bag.t || bag.TikTok || bag.tiktok || 0);
    return 0;
  };

  return (
    <div className="space-y-6">
      <div className="rounded bg-white p-4 text-sm text-gray-600 shadow">
        Daily report structure: Date + Subs (Platform / Plan IDs / Parameter-UTM). Columns are live attribution values.
        {' '}Total Subs in range: <span className="font-semibold text-gray-900">{report.metadata?.totalSubs || 0}</span>
        {' · '}{report.metadata?.from} to {report.metadata?.to} · {report.metadata?.timezone}
      </div>

      <section className="space-y-3">
        <div className="rounded bg-[#0C7240] px-4 py-2 text-sm font-semibold text-white">Daily report</div>
        <div className="overflow-x-auto rounded border border-gray-300 bg-white">
          {!dailyWide.length ? (
            <p className="p-6 text-sm text-gray-500">No successful subscriptions in this range.</p>
          ) : (
            <table className="min-w-full border-collapse text-sm">
              <thead>
                <tr>
                  <th className={reportTh} rowSpan={3}>Date</th>
                  <th className={reportTh} colSpan={Math.max(subsColSpan, 1)}>Subs</th>
                </tr>
                <tr>
                  {platformColSpan > 0 && <th className={reportTh} colSpan={platformColSpan}>Platform</th>}
                  {packageColSpan > 0 && <th className={reportTh} colSpan={packageColSpan}>Plan IDs</th>}
                  {parameterColSpan > 0 && <th className={reportTh} colSpan={parameterColSpan}>Parameter/UTM</th>}
                  {hasEndTotalSub && <th className={reportTh} rowSpan={2}>Total Sub</th>}
                </tr>
                <tr>
                  {platforms.map((key) => <th key={`daily-p-${key}`} className={reportTh}>{key}</th>)}
                  {hasPlatformTotalSub && <th className={reportTh}>Total Sub</th>}
                  {packages.map((key) => <th key={`daily-pkg-${key}`} className={reportTh}>{key}</th>)}
                  {parameters.map((key) => <th key={`daily-par-${key}`} className={reportTh}>{key}</th>)}
                </tr>
              </thead>
              <tbody>
                {dailyWide.map((row) => (
                  <tr key={row.date}>
                    <td className={reportTdLeft}>{row.date}</td>
                    {rawPlatforms.map((key, index) => (
                      <td key={`daily-p-${row.date}-${key}`} className={reportTd}>
                        {cellForDim(row, 'platform', key, platforms[index])}
                      </td>
                    ))}
                    {hasPlatformTotalSub && (
                      <td className={`${reportTd} font-semibold`}>{rowTotal(row)}</td>
                    )}
                    {rawPackages.map((key, index) => (
                      <td key={`daily-pkg-${row.date}-${key}`} className={reportTd}>
                        {cellForDim(row, 'package', key, packages[index])}
                      </td>
                    ))}
                    {rawParameters.map((key, index) => (
                      <td key={`daily-par-${row.date}-${key}`} className={reportTd}>
                        {cellForDim(row, 'parameter', key, parameters[index])}
                      </td>
                    ))}
                    {hasEndTotalSub && (
                      <td className={`${reportTd} font-semibold`}>{rowTotal(row)}</td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </section>

      <section className="space-y-3">
        <div className="rounded bg-[#0C7240] px-4 py-2 text-sm font-semibold text-white">Detailed View</div>
        <div className="grid gap-4 xl:grid-cols-2">
          <div className="space-y-3">
            {rawPlatforms.length === 0 ? (
              <div className="rounded border border-gray-300 bg-white p-4 text-sm text-gray-500">No platform slices yet.</div>
            ) : rawPlatforms.map((platform, index) => (
              <DetailedSliceTable
                key={platform}
                title={displayPlatformDim(platform)}
                rows={
                  report.detailed?.byPlatform?.[platform]
                  || report.detailed?.byPlatform?.[platforms[index]]
                  || report.detailed?.byPlatform?.[displayPlatformDim(platform)]
                  || []
                }
                columns={rawParameters}
              />
            ))}
          </div>
          <div className="space-y-3">
            {rawPackages.length === 0 ? (
              <div className="rounded border border-gray-300 bg-white p-4 text-sm text-gray-500">No package / plan id slices yet.</div>
            ) : rawPackages.map((pkg, index) => (
              <DetailedSliceTable
                key={pkg}
                title={displayPackageDim(pkg)}
                rows={report.detailed?.byPackage?.[pkg] || report.detailed?.byPackage?.[packages[index]] || []}
                columns={rawParameters}
              />
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default function PremiumReports() {
  const {accessToken, adminSession, hasCapability} = useContext(AuthContext);
  const [searchParams, setSearchParams] = useSearchParams();
  const defaults = rangeForPreset('30d');
  const [summary, setSummary] = useState(null);
  const [detail, setDetail] = useState({rows: [], pagination: null, totals: null});
  const [campaignReport, setCampaignReport] = useState(null);
  const [summaryError, setSummaryError] = useState('');
  const [detailError, setDetailError] = useState('');
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState('');
  const superAdmin = adminSession?.role === 'Super Admin';
  const canRead = superAdmin || hasCapability(ADMIN_CAPABILITIES.PREMIUM_REPORTS_READ);
  const canExport = superAdmin || hasCapability(ADMIN_CAPABILITIES.PREMIUM_REPORTS_EXPORT);
  const tab = TABS.includes(searchParams.get('report')) ? searchParams.get('report') : 'summary';

  const filters = useMemo(() => ({
    from: searchParams.get('from') || defaults.from,
    to: searchParams.get('to') || defaults.to,
    interval: searchParams.get('interval') === 'month' ? 'month' : 'day',
    planCode: searchParams.get('planCode') || '',
    purpose: searchParams.get('purpose') || '',
    status: searchParams.get('status') || '',
    autoRenew: searchParams.get('autoRenew') || '',
    q: searchParams.get('q') || '',
    page: Number(searchParams.get('page') || 1),
    limit: 25,
  }), [searchParams, defaults.from, defaults.to]);

  const api = useMemo(() => axios.create({
    baseURL: process.env.REACT_APP_API_URL,
    headers: {Authorization: `Bearer ${accessToken}`},
    timeout: 30000,
  }), [accessToken]);

  const setFilter = (name, value) => {
    const next = new URLSearchParams(searchParams);
    if (value === '' || value === null) next.delete(name); else next.set(name, String(value));
    if (name !== 'page') next.set('page', '1');
    setSearchParams(next);
  };

  const applyPreset = (preset) => {
    const range = rangeForPreset(preset);
    const next = new URLSearchParams(searchParams);
    next.set('from', range.from); next.set('to', range.to); next.set('page', '1');
    setSearchParams(next);
  };

  const load = useCallback(async () => {
    if (!canRead) return;
    setLoading(true); setSummaryError(''); setDetailError('');
    const detailQuery = new URLSearchParams(Object.entries(filters).filter(([, value]) => value !== '')).toString();
    const summaryFilters = ['summary', 'payments'].includes(tab) ? filters : {...filters, status: ''};
    const summaryQuery = new URLSearchParams(Object.entries(summaryFilters).filter(([, value]) => value !== '')).toString();
    const summaryRequest = api.get(`api/premium/admin/reports/summary?${summaryQuery}`)
      .then((response) => setSummary(response.data.report))
      .catch((error) => setSummaryError(error?.response?.data?.message || 'Premium summary is unavailable.'));
    let detailRequest = Promise.resolve();
    if (tab === 'campaign-attribution') {
      detailRequest = api.get(`${ENDPOINTS[tab]}?${detailQuery}`)
        .then((response) => setCampaignReport(response.data.report || null))
        .catch((error) => {
          setCampaignReport(null);
          setDetailError(error?.response?.data?.message || 'Campaign attribution report is unavailable.');
        });
    } else if (tab !== 'summary') {
      detailRequest = api.get(`${ENDPOINTS[tab]}?${detailQuery}`)
        .then((response) => setDetail({rows: response.data.rows || [], pagination: response.data.pagination || null, totals: response.data.totals || null}))
        .catch((error) => setDetailError(error?.response?.data?.message || 'This report section is unavailable.'));
    }
    await Promise.allSettled([summaryRequest, detailRequest]);
    setLoading(false);
  }, [api, canRead, filters, tab]);

  useEffect(() => { void load(); }, [load]);

  const download = async (type) => {
    setExporting(type);
    try {
      const exportFilters = type === 'summary' && !['summary', 'payments'].includes(tab) ? {...filters, status: ''} : filters;
      const response = await api.post('api/premium/admin/reports/export', {type, filters: exportFilters}, {responseType: 'blob'});
      const disposition = response.headers['content-disposition'] || '';
      const filename = disposition.match(/filename="?([^";]+)"?/)?.[1] || `premium-${type}.csv`;
      const url = URL.createObjectURL(response.data);
      const anchor = document.createElement('a'); anchor.href = url; anchor.download = filename; document.body.appendChild(anchor); anchor.click(); anchor.remove();
      URL.revokeObjectURL(url);
    } catch (error) {
      setDetailError(error?.response?.data?.message || 'Unable to export this report.');
    } finally {
      setExporting('');
    }
  };

  if (!canRead) return <div className="rounded bg-white p-6 shadow">Premium reporting capability is required.</div>;
  const values = summary?.summary || {};
  const definitions = summary?.metadata?.definitions || {};
  const maxSeries = Math.max(1, ...(summary?.series || []).map((row) => Number(row.grossCollections || 0)));

  return (
    <div className="space-y-5">
      <section className="rounded bg-white p-5 shadow">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div><h2 className="text-xl font-semibold">Premium reporting test</h2><p className="text-sm text-gray-600">Finance and subscription operations. Times are shown in Asia/Karachi.</p></div>
          <button onClick={load} disabled={loading} className="rounded border px-4 py-2">{loading ? 'Refreshing…' : 'Refresh'}</button>
        </div>
        <div className="mt-4 flex flex-wrap gap-2">{['today', 'yesterday', '7d', '30d', 'month', 'previous'].map((preset) => <button key={preset} onClick={() => applyPreset(preset)} className="rounded bg-gray-100 px-3 py-2 text-sm">{preset === '7d' ? 'Last 7 days' : preset === '30d' ? 'Last 30 days' : preset === 'month' ? 'Current month' : preset === 'previous' ? 'Previous month' : preset[0].toUpperCase() + preset.slice(1)}</button>)}</div>
        <div className="mt-4 grid gap-3 md:grid-cols-4 xl:grid-cols-8">
          <label className="text-xs">From<input type="date" value={filters.from} onChange={(event) => setFilter('from', event.target.value)} className="mt-1 w-full rounded border p-2 text-sm" /></label>
          <label className="text-xs">To<input type="date" value={filters.to} onChange={(event) => setFilter('to', event.target.value)} className="mt-1 w-full rounded border p-2 text-sm" /></label>
          <label className="text-xs">Grouping<select value={filters.interval} onChange={(event) => setFilter('interval', event.target.value)} className="mt-1 w-full rounded border p-2 text-sm"><option value="day">Daily</option><option value="month">Monthly</option></select></label>
          <label className="text-xs">Plan<input value={filters.planCode} onChange={(event) => setFilter('planCode', event.target.value)} placeholder="monthly" className="mt-1 w-full rounded border p-2 text-sm" /></label>
          <label className="text-xs">Purpose<select value={filters.purpose} onChange={(event) => setFilter('purpose', event.target.value)} className="mt-1 w-full rounded border p-2 text-sm"><option value="">All</option><option value="activation">Activation</option><option value="renewal">Renewal</option></select></label>
          <label className="text-xs">Status<input value={filters.status} onChange={(event) => setFilter('status', event.target.value)} placeholder="completed" className="mt-1 w-full rounded border p-2 text-sm" /></label>
          <label className="text-xs">Auto-renew<select value={filters.autoRenew} onChange={(event) => setFilter('autoRenew', event.target.value)} className="mt-1 w-full rounded border p-2 text-sm"><option value="">All</option><option value="true">Enabled</option><option value="false">Disabled</option></select></label>
          <label className="text-xs">Customer<input value={filters.q} onChange={(event) => setFilter('q', event.target.value)} placeholder="Name, phone, ID" className="mt-1 w-full rounded border p-2 text-sm" /></label>
        </div>
      </section>

      {summaryError && <div className="rounded bg-red-50 p-4 text-sm text-red-700">{summaryError}</div>}
      {summary && <>
        <div className="grid gap-4 md:grid-cols-3 xl:grid-cols-6">
          <Metric label="Gross collections" value={money(values.grossCollections)} help={definitions.grossCollections} />
          <Metric label="Refunds" value={money(values.refunds)} help={definitions.refunds} />
          <Metric label="Net collections" value={money(values.netCollections)} help={definitions.netCollections} />
          <Metric label="MRR" value={money(values.mrr)} help={definitions.mrr} />
          <Metric label="ARR" value={money(values.arr)} help={definitions.arr} />
          <Metric label="Benefit cost" value={money(values.benefitCost)} help={definitions.benefitCost} />
          <Metric label="Paid activations" value={values.newActivations || 0} />
          <Metric label="Successful renewals" value={values.successfulRenewals || 0} />
          <Metric label="Failed renewals" value={values.failedRenewals || 0} />
          <Metric label="Renewal success" value={percent(values.renewalSuccessRate)} help={definitions.renewalSuccessRate} />
          <Metric label="Auto-renew users" value={values.autoRenewUsers || 0} help={definitions.autoRenewUsers} />
          <Metric label="Actual recurring users" value={values.actualRecurringCustomers || 0} help={definitions.actualRecurringCustomers} />
        </div>
        <div className="rounded bg-white p-5 shadow">
          <div className="flex items-center justify-between"><div><h3 className="font-semibold">Collections trend</h3><p className="text-xs text-gray-500">{summary.metadata.from} to {summary.metadata.to} · {summary.metadata.schemaVersion}</p></div>{canExport && <button onClick={() => download('summary')} disabled={Boolean(exporting)} className="rounded bg-[#0C7240] px-3 py-2 text-sm text-white">Export summary</button>}</div>
          <div className="mt-4 space-y-3">{(summary.series || []).map((row) => <div key={row.bucket}><div className="flex justify-between text-xs"><span>{row.bucket}</span><span>{money(row.netCollections)}</span></div><div className="mt-1 h-3 rounded bg-gray-100"><div className="h-3 rounded bg-[#0C7240]" style={{width: `${Math.max(1, (Number(row.grossCollections || 0) / maxSeries) * 100)}%`}} /></div></div>)}</div>
        </div>
      </>}

      <section className="rounded bg-white p-4 shadow"><div className="flex flex-wrap gap-2">{TABS.map((item) => <button key={item} onClick={() => setFilter('report', item)} className={`rounded px-3 py-2 text-sm capitalize ${tab === item ? 'bg-[#0C7240] text-white' : 'bg-gray-100'}`}>{item.replace(/-/g, ' ')}</button>)}</div></section>
      {detailError && <div className="rounded bg-yellow-50 p-4 text-sm text-yellow-800">{detailError}</div>}
      {tab === 'campaign-attribution' && <>
        <div className="flex justify-end">{canExport && <button onClick={() => download('campaign-attribution')} disabled={Boolean(exporting)} className="rounded bg-[#0C7240] px-4 py-2 text-sm text-white">{exporting === 'campaign-attribution' ? 'Generating…' : 'Download CSV'}</button>}</div>
        <CampaignAttributionReport report={campaignReport} />
      </>}
      {tab !== 'summary' && tab !== 'campaign-attribution' && <>
        <div className="flex justify-end">{canExport && <button onClick={() => download(tab)} disabled={Boolean(exporting)} className="rounded bg-[#0C7240] px-4 py-2 text-sm text-white">{exporting === tab ? 'Generating…' : 'Download CSV'}</button>}</div>
        {detail.totals && <div className="grid gap-4 md:grid-cols-5"><Metric label="Gross credited" value={money(detail.totals.credited)} /><Metric label="Reversed" value={money(detail.totals.reversed)} /><Metric label="Failed" value={money(detail.totals.failed)} /><Metric label="Reversal required" value={money(detail.totals.reversalRequired)} /><Metric label="Net cost" value={money(detail.totals.netCost)} /></div>}
        <DataTable tab={tab} rows={detail.rows || []} />
        {detail.pagination && detail.pagination.total > detail.pagination.limit && <div className="flex justify-between rounded bg-white p-3 text-sm shadow"><button disabled={filters.page <= 1} onClick={() => setFilter('page', filters.page - 1)} className="rounded border px-3 py-1">Previous</button><span>Page {filters.page} · {detail.pagination.total} records</span><button disabled={filters.page * detail.pagination.limit >= detail.pagination.total} onClick={() => setFilter('page', filters.page + 1)} className="rounded border px-3 py-1">Next</button></div>}
      </>}
    </div>
  );
}
